/**
 * Edge Functions Utility
 *
 * Provides a consistent interface for calling Supabase Edge Functions
 * with automatic URL resolution based on environment configuration.
 */

import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js'
import {
  supabase,
  getEdgeFunctionsUrl,
  getAnonKey,
  getSupabaseUrl,
} from './supabase'

export type EdgeFunctionErrorCode =
  | 'session_expired'
  | 'unauthorized'
  | 'network_error'
  | 'unknown'

export type EdgeFunctionAuthRecoveryMode = 'strict' | 'checkout_resilient'

export interface EdgeFunctionOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: Record<string, unknown>
  headers?: Record<string, string>
  /** If true, automatically include the user's auth token */
  withAuth?: boolean
  /** Override auth recovery behavior. Defaults are set per function. */
  authRecoveryMode?: EdgeFunctionAuthRecoveryMode
  /** @deprecated Use authRecoveryMode instead. */
  allowAnonFallback?: boolean
}

export interface EdgeFunctionResponse<T = unknown> {
  data: T | null
  error: string | null
  errorCode: EdgeFunctionErrorCode | null
  status: number
}

const AUTH_RECOVERY_MODE_BY_FUNCTION: Record<string, EdgeFunctionAuthRecoveryMode> = {
  'create-checkout-session': 'checkout_resilient',
}

function getDefaultAuthRecoveryMode(functionName: string): EdgeFunctionAuthRecoveryMode {
  return AUTH_RECOVERY_MODE_BY_FUNCTION[functionName] || 'strict'
}

function getErrorCodeFromStatus(status: number): EdgeFunctionErrorCode {
  if (status === 401) return 'unauthorized'
  if (status === 0) return 'network_error'
  return 'unknown'
}

function buildSessionExpiredResponse<T = unknown>(error: string): EdgeFunctionResponse<T> {
  return {
    data: null,
    error,
    errorCode: 'session_expired',
    status: 401,
  }
}

function logAuthRecovery(
  functionName: string,
  step: string,
  details?: Record<string, unknown>
) {
  if (!import.meta.env.DEV) return
  if (details) {
    console.info(`[edge-functions:${functionName}] ${step}`, details)
    return
  }
  console.info(`[edge-functions:${functionName}] ${step}`)
}

function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, '')
}

async function parseErrorPayload(response?: Response): Promise<string | null> {
  if (!response) return null

  const contentType = response.headers.get('Content-Type') || ''
  if (contentType.includes('application/json')) {
    const payload = await response.clone().json().catch(() => null) as
      | { error?: string; message?: string }
      | null
    return payload?.error || payload?.message || null
  }

  const text = await response.clone().text().catch(() => '')
  return text || null
}

/**
 * Call a Supabase Edge Function
 */
export async function callEdgeFunction<T = unknown>(
  functionName: string,
  options: EdgeFunctionOptions = {}
): Promise<EdgeFunctionResponse<T>> {
  const {
    method = 'GET',
    body,
    headers = {},
    withAuth = false,
    authRecoveryMode,
    allowAnonFallback = false,
  } = options

  const configuredFunctionsUrl = normalizeUrl(getEdgeFunctionsUrl())
  const supabaseClientFunctionsUrl = normalizeUrl(`${getSupabaseUrl()}/functions/v1`)
  const useSupabaseInvoke = configuredFunctionsUrl === supabaseClientFunctionsUrl
  const effectiveRecoveryMode = withAuth
    ? authRecoveryMode ||
      (allowAnonFallback ? 'checkout_resilient' : getDefaultAuthRecoveryMode(functionName))
    : 'strict'
  const preferFetchInvoke = effectiveRecoveryMode === 'checkout_resilient'

  // Get auth token if requested
  const anonKey = getAnonKey()
  let authToken = anonKey
  let usedSessionToken = false

  if (withAuth) {
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.access_token) {
      authToken = session.access_token
      usedSessionToken = true
    }
  }

  if (withAuth && effectiveRecoveryMode === 'strict' && !usedSessionToken) {
    return buildSessionExpiredResponse('Your session is missing. Please sign in again.')
  }

  // For strict authenticated endpoints, verify/refresh token up-front to avoid noisy
  // gateway 401s when stale tokens are cached in local storage.
  if (withAuth && usedSessionToken && effectiveRecoveryMode === 'strict') {
    const { data: userData, error: userError } = await supabase.auth.getUser(authToken)
    if (userError || !userData.user) {
      logAuthRecovery(functionName, 'Initial session token invalid during preflight. Attempting refresh.', {
        error: userError?.message || 'No user returned',
      })

      const { data, error } = await supabase.auth.refreshSession()
      const refreshedToken = data.session?.access_token

      if (!error && refreshedToken) {
        authToken = refreshedToken
      } else {
        return buildSessionExpiredResponse('Your session expired. Please sign in again.')
      }
    }
  }

  // For checkout we prefer a freshly minted token up-front to minimize 401 retries.
  if (effectiveRecoveryMode === 'checkout_resilient' && usedSessionToken) {
    const { data, error } = await supabase.auth.refreshSession()
    const refreshedToken = data.session?.access_token
    if (!error && refreshedToken) {
      authToken = refreshedToken
      logAuthRecovery(functionName, 'Preflight session refresh succeeded for checkout.')
    } else if (error) {
      logAuthRecovery(functionName, 'Preflight session refresh failed for checkout.', {
        error: error.message,
      })
    }
  }

  async function invokeViaSupabase(token: string): Promise<EdgeFunctionResponse<T>> {
    const requestHeaders = {
      apikey: anonKey,
      Authorization: `Bearer ${token}`,
      ...headers,
    }

    const { data, error, response } = await supabase.functions.invoke(functionName, {
      method,
      body,
      headers: requestHeaders,
    })

    if (error) {
      const status = response?.status ?? 0
      const payloadError = await parseErrorPayload(response)

      if (error instanceof FunctionsHttpError) {
        return {
          data: null,
          error: payloadError || `HTTP ${status}: ${response?.statusText || 'Request failed'}`,
          errorCode: getErrorCodeFromStatus(status),
          status,
        }
      }

      if (error instanceof FunctionsRelayError || error instanceof FunctionsFetchError) {
        return {
          data: null,
          error: error.message || 'Failed to invoke edge function',
          errorCode: getErrorCodeFromStatus(status),
          status,
        }
      }

      return {
        data: null,
        error: payloadError || (error instanceof Error ? error.message : 'Unknown error'),
        errorCode: getErrorCodeFromStatus(status),
        status,
      }
    }

    return {
      data: data as T,
      error: null,
      errorCode: null,
      status: response?.status ?? 200,
    }
  }

  async function invokeViaFetch(token: string): Promise<EdgeFunctionResponse<T>> {
    const requestHeaders = {
      apikey: anonKey,
      Authorization: `Bearer ${token}`,
      ...headers,
    }

    // Compatibility path for split local/prod routing (local functions + prod DB).
    try {
      const response = await fetch(`${configuredFunctionsUrl}/${functionName}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...requestHeaders,
        },
        body: body ? JSON.stringify(body) : undefined,
      })

      const data = await response.json().catch(() => null)

      if (!response.ok) {
        return {
          data: null,
          error: data?.error || data?.message || `HTTP ${response.status}: ${response.statusText}`,
          errorCode: getErrorCodeFromStatus(response.status),
          status: response.status,
        }
      }

      return {
        data: data as T,
        error: null,
        errorCode: null,
        status: response.status,
      }
    } catch (err) {
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Unknown error',
        errorCode: 'network_error',
        status: 0,
      }
    }
  }

  // When withAuth is false, always use direct fetch to prevent the Supabase SDK
  // from injecting stale/invalid session tokens into the Authorization header.
  // invokeViaSupabase is only valuable for its session token management (withAuth calls).
  const invoke = withAuth && !preferFetchInvoke && useSupabaseInvoke
    ? invokeViaSupabase
    : invokeViaFetch

  let result = await invoke(authToken)
  let sessionRejectedAfterRefresh = false

  if (withAuth && usedSessionToken && result.status === 401) {
    logAuthRecovery(functionName, 'Session token rejected. Attempting refresh.')
    const { data, error } = await supabase.auth.refreshSession()
    const refreshedToken = data.session?.access_token

    if (!error && refreshedToken) {
      logAuthRecovery(functionName, 'Session refresh succeeded. Retrying with refreshed token.')
      result = await invoke(refreshedToken)
    } else {
      logAuthRecovery(functionName, 'Session refresh failed.', {
        error: error?.message || 'No refreshed token returned',
      })
    }
  }

  if (withAuth && usedSessionToken && result.status === 401) {
    sessionRejectedAfterRefresh = true
    logAuthRecovery(functionName, 'Session still unauthorized after refresh.')

    if (effectiveRecoveryMode === 'checkout_resilient') {
      logAuthRecovery(functionName, 'Retrying with anon token for resilient checkout path.')
      result = await invoke(anonKey)
    }
  }

  if (
    effectiveRecoveryMode === 'strict' &&
    sessionRejectedAfterRefresh &&
    result.status === 401 &&
    result.error
  ) {
    return {
      ...result,
      error: 'Your session is invalid or expired. Please sign out and sign in again.',
      errorCode: 'session_expired',
    }
  }

  return result
}

/**
 * Check if edge functions are available
 */
export async function checkEdgeFunctions(): Promise<{
  available: boolean
  environment: 'local' | 'production' | 'unknown'
  error?: string
}> {
  const result = await callEdgeFunction<{
    status: string
    environment: 'local' | 'production'
  }>('health')

  if (result.error) {
    return {
      available: false,
      environment: 'unknown',
      error: result.error,
    }
  }

  return {
    available: result.data?.status === 'ok',
    environment: result.data?.environment || 'unknown',
  }
}
